/**
 * Formatting for exact numeric strings.
 *
 * Share counts and money arrive from the API as strings precisely so nothing
 * rounds them. These helpers format for *display* without ever parsing back to a
 * Number — the moment you call parseFloat on a share count you have thrown away
 * the guarantee the whole schema is built to provide.
 *
 * Grouping is done by string manipulation on the integer part for that reason.
 */

/** "12345678" → "12,345,678". Handles decimals and signs without Number(). */
export function groupDigits(value) {
  if (value === null || value === undefined || value === '') return '—';
  const text = String(value);
  const negative = text.startsWith('-');
  const unsigned = negative ? text.slice(1) : text;
  const [whole, fraction] = unsigned.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}${fraction ? `.${fraction}` : ''}`;
}

/** Whole-share display. */
export function shares(value) {
  if (value === null || value === undefined || value === '') return '—';
  return groupDigits(value);
}

/** "9" → "10", "99" → "100". String carry, so the integer part stays exact. */
function incrementDigits(digits) {
  const out = digits.split('');
  for (let i = out.length - 1; i >= 0; i -= 1) {
    if (out[i] === '9') {
      out[i] = '0';
    } else {
      out[i] = String(Number(out[i]) + 1);
      return out.join('');
    }
  }
  return `1${out.join('')}`;
}

/**
 * Round a decimal *string* to `places`, half-up, without ever touching a float.
 *
 * Truncating was the original implementation and it was wrong: 61.016950
 * rendered as "61.01%" and $1,234.567 as "$1,234.56". Understating a figure on a
 * cap table is not a rounding preference, it is a defect — and doing the round
 * via Number() would defeat the exact-string contract the API goes to such
 * lengths to provide, since a share count or a large valuation can exceed the
 * exact range of a double.
 */
function roundDecimalString(text, places) {
  const negative = text.startsWith('-');
  const unsigned = negative ? text.slice(1) : text;
  const [rawWhole, rawFraction = ''] = unsigned.split('.');
  let whole = rawWhole === '' ? '0' : rawWhole;

  if (places === 0) {
    if (rawFraction.charCodeAt(0) >= 53 /* '5' */) whole = incrementDigits(whole);
    return { negative, whole, fraction: '' };
  }

  if (rawFraction.length <= places) {
    return { negative, whole, fraction: rawFraction.padEnd(places, '0') };
  }

  let keep = rawFraction.slice(0, places);
  if (rawFraction.charCodeAt(places) >= 53 /* '5' */) {
    const bumped = incrementDigits(keep);
    if (bumped.length > keep.length) {
      // The carry ran off the end of the fraction (e.g. .99 → 1.00).
      whole = incrementDigits(whole);
      keep = bumped.slice(1);
    } else {
      keep = bumped;
    }
  }
  return { negative, whole, fraction: keep };
}

/**
 * Currency. `places` is explicit at the call site because a per-share price
 * needs more places than a total.
 */
export function money(value, { places = 2, symbol = '$' } = {}) {
  if (value === null || value === undefined || value === '') return '—';
  const { negative, whole, fraction } = roundDecimalString(String(value), places);
  const body = `${groupDigits(whole)}${fraction ? `.${fraction}` : ''}`;
  return `${negative ? '-' : ''}${symbol}${body}`;
}

/**
 * A per-share price. Shows enough places to be meaningful without a wall of
 * zeros: significant digits are preserved, trailing zeros past 4dp dropped.
 */
export function pricePerShare(value) {
  if (value === null || value === undefined || value === '') return '—';
  const text = String(value);
  const [whole, fraction = ''] = text.split('.');
  if (!fraction) return money(text, { places: 2 });
  const meaningful = fraction.replace(/0+$/, '');
  const places = Math.min(Math.max(meaningful.length, 2), 10);
  return money(text, { places });
}

/** "23.728813" → "23.73%". Rounds, for the same reason money() does. */
export function percent(value, { places = 2 } = {}) {
  if (value === null || value === undefined || value === '') return '—';
  const { negative, whole, fraction } = roundDecimalString(String(value), places);
  return `${negative ? '-' : ''}${groupDigits(whole)}${fraction ? `.${fraction}` : ''}%`;
}

/** Compact axis labels: 12500000 → "12.5M". Display-only, never for maths. */
export function compact(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(String(value).split('.')[0]);
  if (!Number.isFinite(n)) return groupDigits(value);
  const abs = Math.abs(n);
  // One decimal until the mantissa reaches three digits, so $12.5M keeps its
  // half. The earlier thresholds dropped it and rendered "13M", which is a
  // misleading axis label on a waterfall.
  const scale = (divisor, suffix) => {
    const scaled = n / divisor;
    return `${scaled.toFixed(Math.abs(scaled) >= 100 ? 0 : 1)}${suffix}`;
  };
  if (abs >= 1e12) return scale(1e12, 'T');
  if (abs >= 1e9) return scale(1e9, 'B');
  if (abs >= 1e6) return scale(1e6, 'M');
  if (abs >= 1e3) return scale(1e3, 'k');
  return String(n);
}

export function compactMoney(value) {
  const text = compact(value);
  return text === '—' ? text : `$${text}`;
}

/**
 * A `date`-typed database column (issue date, board approval date, start
 * date...) has no time-of-day component at all, but it always arrives here
 * serialized as a UTC-midnight instant. Formatting that instant in the
 * viewer's own local timezone -- the default for `toLocaleDateString` --
 * shifts it back a calendar day for anyone west of UTC, which is most of
 * the Americas. `timeZone: 'UTC'` renders the calendar date exactly as
 * stored, regardless of where the viewer is.
 */
export function date(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * "Today," in the browser's own local calendar -- to default a
 * `<input type="date">` field, not `new Date().toISOString().slice(0, 10)`,
 * which computes UTC's current date instead. UTC is ahead of every
 * timezone in the Americas, so for a chunk of every local evening (the
 * exact window depends on the offset) that computes tomorrow, not today --
 * the same instant-vs-calendar-date confusion `date()` above fixes for
 * display, in the opposite direction: a default value that's silently one
 * day ahead of what the person filling out the form actually means by
 * "today."
 *
 * Takes `now` as an optional argument (defaulting to the real current
 * instant) so a test can pin it without mocking the global Date
 * constructor.
 */
export function localToday(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function dateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** SECURITY_TYPE → "Security type"-ish label. */
export function humanize(value) {
  if (!value) return '—';
  const words = String(value).replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * ENUM_VALUE → "enum value", for the lowercase badge labels used throughout.
 *
 * Exists because pages were calling `row.some_enum.toLowerCase()` directly, which
 * throws a TypeError the moment a column is nullable or an API field is renamed —
 * and a throw during render unmounts the whole view. A missing enum should show a
 * dash, not blank the tab.
 */
export function enumLabel(value) {
  if (!value) return '—';
  return String(value).replace(/_/g, ' ').toLowerCase();
}

const SECURITY_LABELS = {
  COMMON_STOCK: 'Common',
  PREFERRED_STOCK: 'Preferred',
  OPTION: 'Option',
  RSU: 'RSU',
  WARRANT: 'Warrant',
  SAFE: 'SAFE',
  CONVERTIBLE_NOTE: 'Note',
};

export function securityType(value) {
  return SECURITY_LABELS[value] ?? humanize(value);
}

const VALUATION_LABELS = {
  FMV_409A: '409A FMV',
  BOARD: 'Board-determined',
  POST_MONEY: 'Post-money',
};

export function valuationKind(value) {
  return VALUATION_LABELS[value] ?? humanize(value);
}

/** BigInt-safe comparator for sorting string share counts descending. */
export function compareShareStrings(a, b) {
  const left = BigInt(a || '0');
  const right = BigInt(b || '0');
  if (left === right) return 0;
  return left > right ? -1 : 1;
}

/** Sum a list of exact integer strings without going through Number. */
export function sumShareStrings(values) {
  return values.reduce((total, value) => total + BigInt(value || '0'), 0n).toString();
}
